import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Play, Clock, Users, Award, MessageCircle, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { usePricing, formatPrice, getDisplayPrices } from '../context/PricingContext';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import { BreadcrumbSchema, CourseSchema } from '../components/StructuredData';

const SubjectDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [subject, setSubject] = useState(null);
  const [tutor, setTutor] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [selectedClassType, setSelectedClassType] = useState('group');

  const pricing = usePricing();
  const { user } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: subjectData, error: subjectError } = await supabase
          .from('subjects').select('*').eq('slug', slug).single();
        if (subjectError) throw subjectError;
        setSubject(subjectData);

        // Fetch tutor for this subject
        if (subjectData.tutor_name) {
          const { data: tutorData } = await supabase
            .from('tutors').select('*').contains('subjects', [subjectData.name]).single();
          if (tutorData) setTutor(tutorData);
        }

        // Fetch batches for this subject
        const { data: batchData } = await supabase
          .from('batches').select('*').eq('subject_id', subjectData.subject_id);
        if (batchData) setBatches(batchData);
      } catch (e) {
        console.error('Error fetching subject:', e);
        toast.error('Subject not found');
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug, navigate]);

  const handleEnroll = async (batchId) => {
    if (!user) {
      toast.error('Please login to enroll');
      navigate('/login');
      return;
    }

    setEnrolling(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/enrollments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ batch_id: batchId })
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Enrollment failed');
      toast.success('Successfully enrolled!');
      navigate('/dashboard');
    } catch (e) {
      console.error('Enrollment error:', e);
      if (e.message?.includes('Already enrolled')) {
        toast.error('You are already enrolled in this batch');
      } else {
        toast.error('Failed to process enrollment');
      }
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aspire-red"></div>
      </div>
    );
  }

  if (!subject) return null;

  const levelLabel = subject.level === 'IGCSE' ? 'IGCSE' : `A Level (${subject.level})`;
  const backLink = subject.level === 'IGCSE' ? '/igcse' : '/a-levels';
  const prices = getDisplayPrices(subject, pricing);

  const breadcrumbItems = [
    { name: 'Home', href: '/' },
    { name: `${levelLabel} Programs`, href: backLink },
    { name: `${subject.level} ${subject.name}` },
  ];

  return (
    <main data-testid="subject-detail-page">
      <SEO
        title={`${subject.level} ${subject.name}`}
        description={subject.description || `${subject.level} ${subject.name} tuition at Aspire Academy, Colombo. Expert tutors, small batches, Cambridge & Edexcel.`}
        path={`${backLink}/${slug}`}
      />
      <BreadcrumbSchema items={breadcrumbItems} />
      <CourseSchema subject={subject} />
      {/* Breadcrumb */}
      <section className="bg-surface py-6 border-b border-gray-200">
        <div className="container-main">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate(backLink)} 
              className="inline-flex items-center gap-2 text-gray-600 hover:text-aspire-red transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to {subject.level === 'IGCSE' ? 'IGCSE' : 'A Levels'}
            </button>
          </div>
        </div>
      </section>

      {/* Hero */}
      <section className="bg-white py-12 md:py-16">
        <div className="container-main">
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="text-xs font-mono bg-aspire-red/10 text-aspire-red px-3 py-1">{levelLabel}</span>
                <span className="text-xs font-mono bg-gray-100 text-gray-600 px-3 py-1">{subject.exam_board}</span>
                <span className="text-xs font-mono bg-green-100 text-green-700 px-3 py-1">First Class FREE</span>
              </div>
              <h1 className="heading-hero mb-6">{subject.name}</h1>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">{subject.description}</p>

              {/* Subject Details */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-6 mb-8 sm:mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-aspire-red" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold mb-1">Session Duration</h4>
                    <p className="text-gray-600 text-sm">{subject.session_duration || '2 hours'} per session</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-aspire-red" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold mb-1">Group Class Size</h4>
                    <p className="text-gray-600 text-sm">Maximum {subject.max_group_size || 8} students</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-aspire-red" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold mb-1">Exam Board</h4>
                    <p className="text-gray-600 text-sm">{subject.exam_board}</p>
                  </div>
                </div>
              </div>

              {/* Tutor Profile Card */}
              {tutor ? (
                <div className="mb-12">
                  <h3 className="font-heading font-bold text-xl mb-6">Your Tutor</h3>
                  <div className="relative overflow-hidden bg-charcoal">
                    <div className="flex flex-col md:flex-row">
                      {/* Tutor Image - Card Style */}
                      <div className="relative w-full md:w-72 aspect-[3/4] md:aspect-auto md:min-h-[360px] flex-shrink-0">
                        {tutor.image_url ? (
                          <img
                            src={tutor.image_url}
                            alt={tutor.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center min-h-[280px]">
                            <span className="text-white/20 font-heading font-bold text-9xl">
                              {tutor.name?.charAt(0)}
                            </span>
                          </div>
                        )}
                        {/* Subtle gradient overlay on image */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-charcoal/50 hidden md:block" />
                      </div>

                      {/* Tutor Info */}
                      <div className="flex-1 p-4 sm:p-8 md:p-10 flex flex-col justify-center">
                        <p className="text-aspire-red text-xs font-bold uppercase tracking-widest mb-3">
                          {subject.name} Specialist
                        </p>
                        <h4 className="text-white font-heading font-bold text-2xl md:text-3xl mb-3">
                          {tutor.name}
                        </h4>
                        <p className="text-gray-400 text-sm mb-4 font-medium">
                          {tutor.qualification}
                        </p>

                        {/* Stats row */}
                        <div className="flex gap-6 mb-6">
                          {tutor.experience && (
                            <div>
                              <p className="text-white font-heading font-bold text-lg">{tutor.experience}</p>
                              <p className="text-gray-500 text-xs uppercase tracking-wider">Experience</p>
                            </div>
                          )}
                          <div>
                            <p className="text-white font-heading font-bold text-lg">{subject.exam_board}</p>
                            <p className="text-gray-500 text-xs uppercase tracking-wider">Exam Board</p>
                          </div>
                        </div>

                        {tutor.bio && (
                          <p className="text-gray-400 text-sm leading-relaxed italic border-l-2 border-aspire-red pl-4">
                            "{tutor.bio}"
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : subject.tutor_name ? (
                <div className="mb-12">
                  <h3 className="font-heading font-bold text-xl mb-6">Your Tutor</h3>
                  <div className="relative overflow-hidden bg-charcoal">
                    <div className="flex flex-col md:flex-row">
                      <div className="w-full md:w-72 min-h-[200px] md:min-h-[280px] bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center flex-shrink-0">
                        <span className="text-white/20 font-heading font-bold text-9xl">
                          {subject.tutor_name?.charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1 p-4 sm:p-8 md:p-10 flex flex-col justify-center">
                        <p className="text-aspire-red text-xs font-bold uppercase tracking-widest mb-3">
                          {subject.name} Specialist
                        </p>
                        <h4 className="text-white font-heading font-bold text-2xl md:text-3xl mb-3">
                          {subject.tutor_name}
                        </h4>
                        <p className="text-gray-400 text-sm leading-relaxed">
                          Expert tutor for {subject.exam_board} {subject.name} with extensive experience
                          helping students achieve top grades.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Demo Class Info */}
              <div className="bg-green-50 border border-green-200 p-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-500 flex items-center justify-center flex-shrink-0">
                    <Play className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg mb-2">Free Demo Session</h3>
                    <p className="text-gray-600 text-sm">
                      Your first class is always a free demo session! Try our teaching style, meet your tutor,
                      and see if Aspire Academy is the right fit for you - no obligation.
                    </p>
                  </div>
                </div>
              </div>

              {/* Demo Video Placeholder */}
              <div className="mb-12">
                <h3 className="font-heading font-bold text-xl mb-4">Watch Demo Class</h3>
                <div className="relative bg-gray-900 aspect-video flex items-center justify-center" data-testid="demo-video-placeholder">
                  {subject.demo_video_url ? (
                    <video src={subject.demo_video_url} controls className="w-full h-full" />
                  ) : (
                    <div className="text-center">
                      <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Play className="w-10 h-10 text-white" />
                      </div>
                      <p className="text-gray-400">Demo video coming soon</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Course Overview */}
              <div>
                <h3 className="font-heading font-bold text-xl mb-4">Course Overview</h3>
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-600 leading-relaxed">
                    {subject.overview || subject.description}
                  </p>
                  <h4 className="font-heading font-bold text-lg mt-6 mb-3">What You'll Learn</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Complete syllabus coverage for {subject.exam_board}</li>
                    <li>• Past paper practice and exam techniques</li>
                    <li>• Regular assessments and progress tracking</li>
                    <li>• Doubt clearing sessions and one-on-one support</li>
                    <li>• Comprehensive revision materials</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Sidebar - Enrollment Card */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-white border border-gray-200 shadow-lg">
                <div className="p-6">
                  {/* Class Type Selection */}
                  <div className="mb-6">
                    <h4 className="font-heading font-bold text-sm mb-3">Select Class Type</h4>
                    <div className="space-y-2">
                      <button
                        onClick={() => setSelectedClassType('group')}
                        className={`w-full p-4 border text-left transition-all ${
                          selectedClassType === 'group'
                            ? 'border-aspire-red bg-aspire-red/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid="select-group-class"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="font-bold block">Group Class</span>
                            <span className="text-xs text-gray-500">Max {subject.max_group_size || 8} students</span>
                          </div>
                          <span className="font-mono font-bold text-aspire-red">
                            {formatPrice(prices.group, pricing)}
                          </span>
                        </div>
                        <span className="text-xs text-gray-400 mt-1 block">per session</span>
                      </button>

                      {prices.isLocal ? (
                        <>
                          <button
                            onClick={() => setSelectedClassType('individual-physical')}
                            className={`w-full p-4 border text-left transition-all ${
                              selectedClassType === 'individual-physical'
                                ? 'border-aspire-red bg-aspire-red/5'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            data-testid="select-individual-physical"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <span className="font-bold block">Individual (Physical)</span>
                                <span className="text-xs text-gray-500">One-on-one, in-person</span>
                              </div>
                              <span className="font-mono font-bold text-aspire-red">
                                {formatPrice(prices.individualPhysical, pricing)}
                              </span>
                            </div>
                            <span className="text-xs text-gray-400 mt-1 block">per session</span>
                          </button>

                          <button
                            onClick={() => setSelectedClassType('individual-online')}
                            className={`w-full p-4 border text-left transition-all ${
                              selectedClassType === 'individual-online'
                                ? 'border-aspire-red bg-aspire-red/5'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            data-testid="select-individual-online"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <span className="font-bold block">Individual (Online)</span>
                                <span className="text-xs text-gray-500">One-on-one, virtual</span>
                              </div>
                              <span className="font-mono font-bold text-aspire-red">
                                {formatPrice(prices.individualOnline, pricing)}
                              </span>
                            </div>
                            <span className="text-xs text-gray-400 mt-1 block">per session</span>
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setSelectedClassType('individual-online')}
                          className={`w-full p-4 border text-left transition-all ${
                            selectedClassType === 'individual-online'
                              ? 'border-aspire-red bg-aspire-red/5'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          data-testid="select-individual-online"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="font-bold block">Individual Class</span>
                              <span className="text-xs text-gray-500">One-on-one, online</span>
                            </div>
                            <span className="font-mono font-bold text-aspire-red">
                              {formatPrice(prices.individual, pricing)}
                            </span>
                          </div>
                          <span className="text-xs text-gray-400 mt-1 block">per session</span>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3 mb-6">
                    <a 
                      href="https://wa.me/94743652899?text=Hi! I'm interested in the free demo class for the subject"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary w-full flex items-center justify-center gap-2"
                      data-testid="book-demo-btn"
                    >
                      <Play className="w-5 h-5" />
                      Book FREE Demo
                    </a>
                    <a 
                      href="https://wa.me/94743652899"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary w-full flex items-center justify-center gap-2"
                      data-testid="whatsapp-inquiry-btn"
                    >
                      <MessageCircle className="w-5 h-5" />
                      WhatsApp Inquiry
                    </a>
                  </div>

                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="font-heading font-bold mb-4">This Course Includes:</h4>
                    <ul className="space-y-3 text-sm text-gray-600">
                      <li className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Free first demo session
                      </li>
                      <li className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        2-hour sessions
                      </li>
                      <li className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Comprehensive study materials
                      </li>
                      <li className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Past paper practice
                      </li>
                      <li className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Progress tracking
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </main>
  );
};

export default SubjectDetailPage;
